import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteFooter from "@/components/pivotbox/SiteFooter";
import SiteHeader from "@/components/pivotbox/SiteHeader";
import StageBreadcrumb from "@/components/pivotbox/StageBreadcrumb";
import UnlockDiagnosticCTA from "@/components/pivotbox/UnlockDiagnosticCTA";
import "./S1Diagnostic.css";
import "./sdiag/SDiagnosticExtras.css";

export default function S1DiagnosticPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Sample scores for the report preview card (out of 30 each, total max 120)
  const sampleDimensions = [
    { label: "Motivation & Identity",      score: 22, max: 30, band: "Largely Ready", color: "#0B8F75" },
    { label: "Support & Environment",      score: 22, max: 30, band: "Largely Ready", color: "#A64A1A" },
    { label: "Stamina & Decision Pattern", score: 22, max: 30, band: "Largely Ready", color: "#6B3BB2" },
    { label: "Financial & Timing Reality", score: 21, max: 30, band: "Largely Ready", color: "#9E2B2B" },
  ];

  return (
    <div className="s1d-page" data-testid="s1-diagnostic-page">
      <SiteHeader testIdPrefix="s1d" />

      {/* HERO */}
      <section className="s1d-hero" data-testid="s1d-hero">
        <div className="s1d-container">
          <div className="s1d-hero__eyebrow"><span className="s1d-hero__eyebrow-line" /><span>Founder Reality Check &middot; S1</span></div>
          <h1 className="s1d-hero__headline" data-testid="s1d-hero-headline">Before you build,<br /><em>know your ground.</em></h1>
          <p className="s1d-hero__sub">S1 is a structured diagnostic that answers one question: Is your life, right now, positioned to support a founder transition? Not in theory, in practice.</p>
          <div className="s1d-hero__actions">
            <UnlockDiagnosticCTA
              className="s1d-btn-primary"
              testId="s1d-hero-cta"
            />
            <span className="s1d-hero__price">One-time fee &middot; <strong>&nbsp;&#8377;3,999</strong></span>
          </div>

          {/* Stage progression breadcrumb (S0 → S4) */}
          <StageBreadcrumb currentStage="S1" testId="s1d-breadcrumb" />

          <hr className="s1d-hero__divider" />
        </div>
      </section>

      {/* WHY IT MATTERS */}
      <section className="s1d-section" data-testid="s1d-why" id="why">
        <div className="s1d-container">
          <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">Why this question is underasked</span></div>
          <h2 className="s1d-heading">Most professionals evaluate the idea.<br />Very few examine their life.</h2>
          <div className="s1d-why-layout">
            <div className="s1d-why-body">
              <p>Corporate professionals considering entrepreneurship invest significant time evaluating market opportunity, competitive landscape, and business model viability. The structural question, whether their current life can actually support the transition, rarely receives the same rigour.</p>
              <p>S1 surfaces these gaps before the move, while there is still time to close them. A low score does not mean the transition should not happen. It means specific preparation in that area will materially improve the outcome.</p>
              <p>S1 asks <strong>'is your life ready?'</strong>, not <strong>'are you ready?'</strong> Two founders with identical capabilities and identical business concepts can have completely different structural readiness. The one who is structurally unready will face predictable, avoidable failure modes.</p>
            </div>
            <div className="s1d-why-callout">
              <span className="s1d-why-callout__icon">&darr;</span>
              <p>The structural gaps that derail corporate-to-founder transitions are visible before the leap, not after. S1 makes them visible while there is still time to act.</p>
            </div>
          </div>
        </div>
      </section>

      {/* INSIDE S1 — methodology 3-column */}
      <section className="s1d-method" data-testid="s1d-method">
        <div className="s1d-container">
          <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">Inside the diagnostic</span></div>
          <h2 className="s1d-heading">How S1 is built, and how it is scored.</h2>
          <div className="s1d-method__grid">
            <div className="s1d-method__col">
              <div className="s1d-method__label">Methodology</div>
              <p>S1 was developed from structured research across <strong>corporate-to-founder transitions</strong> in B2B services, technology, and consumer categories. The four dimensions reflect the specific failure patterns that appear consistently &mdash; not generic entrepreneurship theory.</p>
            </div>
            <div className="s1d-method__col">
              <div className="s1d-method__label">What it is not</div>
              <p>S1 is not a personality test, a skills inventory, or a motivation quiz. It is a <strong>structural readiness diagnostic</strong> &mdash; built specifically for professionals with 10&ndash;20 years of corporate experience making a high-stakes transition.</p>
            </div>
            <div className="s1d-method__col">
              <div className="s1d-method__label">How it is scored</div>
              <p>Each of the 20 questions is rated 1&ndash;5. <strong>Consolidated scores</strong> and <strong>parameter-wise scores</strong> will be shared with the action plan.</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT IT MEASURES */}
      <section className="s1d-section" data-testid="s1d-dimensions" id="dimensions">
        <div className="s1d-container">
          <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">What S1 measures</span></div>
          <h2 className="s1d-heading">Four dimensions of structural readiness.</h2>
          <p className="s1d-intro">S1 measures whether the founder's current life situation is structurally ready to support the transition to entrepreneurship. It does not assess whether you <em>can</em> build a business. It assesses whether your life, as it exists today, gives you a realistic chance of doing so.</p>

          <div className="s1d-dim-grid" data-testid="s1d-dim-grid">
            {[
              { num: "01", tag: "Motivation", tagClass: "s1d-dim-tag--mot", title: "Is the desire genuine and durable?", desc: "Motivation distinguishes founders moving toward something from founders moving away from their current situation. Both can start a business. Only one reliably continues when the business stops providing escape.", relevance: "Durable intrinsic motivation is the single biggest predictor of founder persistence through year one. Reactive motivation, built on dissatisfaction with a current role, erodes the moment the transition delivers its first serious obstacle." },
              { num: "02", tag: "Financial", tagClass: "s1d-dim-tag--fin", title: "Does the financial position hold?", desc: "Financial readiness is not comfort, it is clarity. Calculated runway, defined stop-losses, worst-case acceptance, family alignment on zero-income scenarios. A founder who feels financially secure but has not done the calculation is not financially ready.", relevance: "Financial gaps are the most common reason corporate-to-founder transitions fail in the first 18 months, and they are almost always visible in advance, while there is still time to address them." },
              { num: "03", tag: "Support", tagClass: "s1d-dim-tag--sup", title: "Is the environment aligned?", desc: "Support is not cheerleading. Genuine alignment from a partner is different from acceptance. Access to experienced guidance is different from enthusiastic friends. People who challenge thinking are different from people who validate decisions.", relevance: "Unsupportive environments erode resilience slowly and silently. A partner who tolerated the decision in year one becomes a source of active pressure in year two, at exactly the moment the business needs full attention." },
              { num: "04", tag: "Stamina", tagClass: "s1d-dim-tag--sta", title: "Does your life have room for this?", desc: "Stamina is not energy. It is the practical and physical capacity to sustain intensive work over 12 to 18 months, given the current shape of your life. Work, family, health, and existing commitments all determine whether year one is viable.", relevance: "Year one of a founder transition is more demanding than most senior corporate roles. Founders who enter that period already stretched face a compounding disadvantage that no amount of motivation overcomes." },
            ].map((d) => (
              <div className="s1d-dim-card" key={d.num} data-testid={`s1d-dim-${d.num}`}>
                <span className="s1d-dim-num">{d.num}</span>
                <span className={`s1d-dim-tag ${d.tagClass}`}>{d.tag}</span>
                <h3 className="s1d-dim-title">{d.title}</h3>
                <p className="s1d-dim-desc">{d.desc}</p>
                <div className="s1d-dim-relevance"><strong>Why it matters for the transition:</strong> {d.relevance}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW S1 HELPS */}
      <section className="s1d-section" data-testid="s1d-outcomes" id="outcomes">
        <div className="s1d-container">
          <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">How S1 helps</span></div>
          <h2 className="s1d-heading">Three ways S1 changes the transition.</h2>
          <p className="s1d-intro">S1 is not an academic exercise. Every result, including a low one, points to a specific, concrete next step.</p>

          <div className="s1d-helps-grid" data-testid="s1d-helps-grid">
            {[
              { num: "01", title: "Surfaces gaps while there is still time to close them", desc: "Most structural readiness gaps, financial runway, family alignment, life capacity, are addressable in 30 to 90 days if identified in advance. S1 identifies them before the move, not after." },
              { num: "02", title: "Gives the transition a stronger foundation", desc: "Founders who enter the transition knowing their structural position make better early decisions. They negotiate time, manage risk, and sequence priorities with clarity, not instinct." },
              { num: "03", title: "Tells you what to do before you leap", desc: "S1 translates your scores into time-bounded actions, specific things to address in the next 30, 60, or 90 days. Not a list of things to \"think about.\" A concrete preparation plan." },
            ].map((h) => (
              <div className="s1d-helps-card" key={h.num}>
                <span className="s1d-helps-num">{h.num}</span>
                <h3 className="s1d-helps-title">{h.title}</h3>
                <p className="s1d-helps-desc">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU RECEIVE — scored report preview */}
      <section className="s1d-section s1d-receive" data-testid="s1d-receive">
        <div className="s1d-container">
          <div className="s1d-label s1d-label--orange"><span className="s1d-label__line" /><span className="s1d-label__text">Part of the full Pivotbox diagnostic</span></div>
          <p className="s1d-receive__callout">S1 assessment is not an independent assessment &mdash; it is the first of <strong>4 integrated assessments</strong> (S1, S2.1, S2.2, S2.3) that together form the Pivotbox diagnostic report.</p>
          <div className="s1d-receive__grid">
            <div className="s1d-receive__body">
              <h2 className="s1d-heading">A scored report across all assessments &mdash; ready the moment you finish.</h2>
              <p className="s1d-intro">S1 feeds into a consolidated Pivotbox diagnostic report alongside S2 assessments. You see your scores across every dimension, your outcome band, what it means, and exactly what to address before you make the move.</p>
              <ul className="s1d-receive__list">
                <li>
                  <span className="s1d-receive__check">&#10003;</span>
                  <div><strong>Outcome band</strong> &mdash; Provides your overall readiness score.</div>
                </li>
                <li>
                  <span className="s1d-receive__check">&#10003;</span>
                  <div><strong>Dimension scores</strong> across all four areas with interpretation specific to your pattern &mdash; not a generic band description.</div>
                </li>
                <li>
                  <span className="s1d-receive__check">&#10003;</span>
                  <div><strong>Action plan</strong> &mdash; specific preparation steps for your lowest-scoring dimensions, time-bounded and concrete.</div>
                </li>
                <li>
                  <span className="s1d-receive__check">&#10003;</span>
                  <div><strong>Cross-assessment signals</strong> &mdash; how your S1 profile connects to and modifies your S2 results in the full report.</div>
                </li>
              </ul>
            </div>

            {/* Sample Report Card — readiness score hero + dimension bars */}
            <div className="s1d-report" data-testid="s1d-report-sample">
              <div className="s1d-report__hero">
                <div className="s1d-report__hero-left">
                  <div className="s1d-report__score">87</div>
                  <div className="s1d-report__score-max">out of 120</div>
                </div>
                <div className="s1d-report__hero-right">
                  <div className="s1d-report__hero-eyebrow">YOUR OVERALL READINESS SCORE</div>
                  <div className="s1d-report__hero-title"><em>S1 Founder Reality Check</em></div>
                  <div className="s1d-report__hero-desc">Strong base with manageable gaps across all four pillars.</div>
                  <div className="s1d-report__band-badge">Largely Ready</div>
                </div>
              </div>
              <div className="s1d-report__bars">
                {sampleDimensions.map((d) => (
                  <div className="s1d-bar2" key={d.label}>
                    <div className="s1d-bar2__head">
                      <span className="s1d-bar2__label">{d.label}</span>
                      <span className="s1d-bar2__value" style={{ color: d.color }}>{d.score}/{d.max}</span>
                    </div>
                    <div className="s1d-bar2__row">
                      <div className="s1d-bar2__track">
                        <div className="s1d-bar2__fill" style={{ width: `${(d.score / d.max) * 100}%`, background: d.color }} />
                      </div>
                      <span className="s1d-bar2__chip">{d.band}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHERE S1 FITS */}
      <section className="s1d-section s1d-section--dark" data-testid="s1d-framework" id="framework">
        <div className="s1d-container">
          <div className="s1d-label"><span className="s1d-label__line" /><span className="s1d-label__text">Where S1 fits</span></div>
          <h2 className="s1d-heading s1d-heading--white">S1 is the foundation.<br />Everything else builds on it.</h2>
          <p className="s1d-intro s1d-intro--light">Pivotbox is a 5S diagnostic framework. S1 establishes structural readiness before S2 examines how you are wired to build and what your transition should look like, S3 deepens your venture design and S4 guides active execution, from first revenue to scaling decisions.</p>

          <div className="s1d-fw-table" data-testid="s1d-fw-table">
            <div className="s1d-fw-row s1d-fw-row--header">
              <div className="s1d-fw-col-stage">Stage</div>
              <div className="s1d-fw-col-name">Assessment</div>
              <div className="s1d-fw-col-q">The question it answers</div>
            </div>
            {[
              { stage: "S0", name: "Founder Signal Test", badge: "Completed", q: "Are you wired to build? Scenario-based signals across founder instincts.", isFree: true },
              { stage: "S1", name: "Founder Reality Check", badge: "You are here", q: "Is your life right now positioned to support this transition?", isCurrent: true },
              { stage: "S2", name: "Founder Diagnostic", q: "Who are you as a founder, how you build, how you hold up, what you can execute.", isGroup: true },
              { stage: "S2.1", name: "Founder Archetype Assessment", q: "How are you naturally wired to build?", isSub: true, isFuture: true },
              { stage: "S2.2", name: "Founder Durability Assessment", q: "How do you respond to failure, setback, and sustained uncertainty?", isSub: true, isFuture: true },
              { stage: "S2.3", name: "Founder Capability & Execution Profile", q: "Do you have the capabilities to execute your intended business model?", isSub: true, isFuture: true },
              { stage: "S3", name: "Strategy Blueprint", q: "Is the venture concept, model, and market validated enough to commit resources?", isGroup: true },
              { stage: "S4", name: "Founder Operating System", q: "How do you go from first revenue to a business that scales?", isGroup: true },
            ].map((row) => (
              <div className={`s1d-fw-row ${row.isCurrent ? "s1d-fw-row--current" : ""} ${row.isFree ? "s1d-fw-row--free" : ""} ${row.isSub ? "s1d-fw-row--sub" : ""} ${row.isFuture ? "s1d-fw-row--future" : ""}`} key={row.stage}>
                <div className="s1d-fw-col-stage"><span className={`s1d-fw-stage ${row.isCurrent ? "s1d-fw-stage--current" : ""}`}>{row.stage}</span></div>
                <div className="s1d-fw-col-name"><strong>{row.name}</strong>{row.badge && <span className={`s1d-fw-badge ${row.isCurrent ? "s1d-fw-badge--current" : ""}`}>{row.badge}</span>}</div>
                <div className="s1d-fw-col-q">{row.q}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REFUND GUARANTEE */}
      <section className="s1d-refund" data-testid="s1d-refund">
        <div className="s1d-container">
          <div className="s1d-refund__card">
            <div className="s1d-refund__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2 4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>
            </div>
            <div className="s1d-refund__body">
              <h3 className="s1d-refund__heading">If it does not deliver, it does not cost.</h3>
              <p className="s1d-refund__sub">Take the full Pivotbox diagnostic. If it does not give you the clarity we promise, we will refund you in full. No questions, no forms, no friction.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="s1d-cta-section" data-testid="s1d-final-cta" id="start">
        <div className="s1d-container s1d-cta-inner">
          <div className="s1d-cta-copy">
            <span className="s1d-cta-logo-wrap" data-testid="s1d-cta-logo">
              <img src="/pivotbox-p-logo.png" alt="Pivotbox" className="s1d-cta-logo" />
              <span className="s1d-cta-logo-text">Pivotbox</span>
            </span>
            <h2 className="s1d-cta-heading">Structural clarity before the commitment.</h2>
            <p className="s1d-cta-sub">Twenty focused questions. A scored report across four dimensions. A clear picture of where you stand today, and exactly what to address before you make the move.</p>
          </div>
          <div className="s1d-cta-right">
            <div className="s1d-cta-offer">
              <span className="s1d-cta-offer__label">Complete diagnostic</span>
              <div className="s1d-cta-offer__prices">
                <span className="s1d-cta-offer__strike">&#8377;7,999</span>
                <span className="s1d-cta-offer__amount">&#8377;3,999</span>
              </div>
            </div>
            <UnlockDiagnosticCTA
              className="s1d-btn-white"
              testId="s1d-final-cta-btn"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
